<template>
  <modal :show="show" @update:show="handleModalClose" class="dark-modal wizard-modal">
    <div class="modal-box">
      <!-- Header -->
      <div class="modal-box-header">
        <h2 class="modal-title">
          {{ isEditMode ? $t('sourceTypes.wizard.editTitle') : $t('sourceTypes.wizard.createTitle') }}
        </h2>
        <div class="step-indicator">
          <span class="step-text">{{ $t('sourceTypes.wizard.step', { current: currentStep, total: 4 }) }}</span>
          <div class="step-dots">
            <span
              v-for="step in 4"
              :key="step"
              class="step-dot"
              :class="{ 'is-active': step === currentStep, 'is-completed': step < currentStep }"
            />
          </div>
        </div>
      </div>

      <!-- Step Content -->
      <div class="step-content">
        <!-- Step 1: Select Parser Type and Input Log -->
        <div v-show="currentStep === 1" class="step-panel">
          <div class="section-header">
            <h3 class="section-title">{{ $t('sourceTypes.parserv2.selectParserAndLog') }}</h3>
            <p class="section-description">{{ $t('sourceTypes.parserv2.selectParserAndLogDescription') }}</p>
          </div>

          <!-- Parser Selection with Dropdown -->
          <div class="field mb-4">
            <label class="label">{{ $t('sourceTypes.parserv2.parserType') }} <span class="has-text-danger">*</span></label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="selectedParserType" @change="handleParserChange" :disabled="loadingParsers">
                  <option value="">{{ loadingParsers ? $t('sourceTypes.parserv2.loadingParsers') : $t('sourceTypes.parserv2.selectParserPlaceholder') }}</option>
                  <option v-for="parser in availableParsers" :key="parser.type" :value="parser.type">
                    {{ parser.name }} ({{ parser.type }})
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Log Input Section -->
          <div class="field">
            <label class="label">{{ $t('sourceTypes.parserv2.sampleLog') }} <span class="has-text-danger">*</span></label>
            <div class="control">
              <textarea
                v-model="sampleLog"
                class="textarea"
                rows="10"
                :placeholder="$t('sourceTypes.parserv2.logPlaceholder')"
                :disabled="isParsing"
              />
            </div>
          </div>

          <div v-if="parseError" class="notification is-danger mt-3">
            {{ parseError }}
          </div>
        </div>

        <!-- Step 2: Field Mapping -->
        <div v-show="currentStep === 2" class="step-panel">
          <div class="section-header">
            <h3 class="section-title">{{ $t('sourceTypes.wizard.fieldMapping') }}</h3>
            <p class="section-description">{{ $t('sourceTypes.parserv2.fieldMappingDescription') }}</p>
          </div>

          <div class="field-mapping-section">
            <div v-if="!parsedFields || Object.keys(parsedFields).length === 0" class="empty-state">
              <p class="has-text-grey-light">{{ $t('sourceTypes.parserv2.noFieldsParsed') }}</p>
            </div>

            <div v-else class="field-mapping-table">
              <table class="table is-fullwidth is-hoverable">
                <thead>
                  <tr>
                    <th style="width: 25%">{{ $t('sourceTypes.parserv2.sourceField') }}</th>
                    <th style="width: 35%">{{ $t('sourceTypes.parserv2.sampleValue') }}</th>
                    <th style="width: 40%">{{ $t('sourceTypes.parserv2.targetField') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(value, field) in parsedFields" :key="field">
                    <td>
                      <code class="field-name">{{ field }}</code>
                    </td>
                    <td>
                      <code class="sample-value">{{ formatFieldValue(value) }}</code>
                    </td>
                    <td>
                      <input
                        v-model="fieldMappings[field]"
                        type="text"
                        class="input"
                        :placeholder="getSuggestedMapping(field)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Step 3: Preview (Optional) -->
        <div v-show="currentStep === 3" class="step-panel">
          <div class="section-header">
            <h3 class="section-title">{{ $t('sourceTypes.wizard.preview') }}</h3>
            <p class="section-description">{{ $t('sourceTypes.parserv2.previewDescription') }}</p>
          </div>

          <div class="preview-section">
            <div class="preview-summary">
              <div class="summary-item">
                <strong>{{ $t('sourceTypes.parserv2.parserType') }}:</strong>
                <span>{{ selectedParserName }}</span>
              </div>
              <div class="summary-item">
                <strong>{{ $t('sourceTypes.parserv2.fieldsDetected') }}:</strong>
                <span>{{ Object.keys(parsedFields || {}).length }}</span>
              </div>
              <div class="summary-item">
                <strong>{{ $t('sourceTypes.parserv2.fieldsMapped') }}:</strong>
                <span>{{ Object.values(fieldMappings).filter(v => v).length }}</span>
              </div>
            </div>

            <div class="preview-mappings mt-4">
              <h4 class="subtitle is-6">{{ $t('sourceTypes.parserv2.mappingsSummary') }}</h4>
              <div v-if="Object.values(fieldMappings).filter(v => v).length === 0" class="has-text-grey-light">
                {{ $t('sourceTypes.parserv2.noMappings') }}
              </div>
              <ul v-else class="mapping-list">
                <li v-for="(target, source) in fieldMappings" :key="source">
                  <span v-if="target">
                    <code>{{ source }}</code> → <code>{{ target }}</code>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Step 4: Final Configuration -->
        <div v-show="currentStep === 4" class="step-panel">
          <div class="section-header">
            <h3 class="section-title">{{ $t('sourceTypes.wizard.finalConfig') }}</h3>
            <p class="section-description">{{ $t('sourceTypes.parserv2.finalConfigDescription') }}</p>
          </div>

          <div class="final-config-section">
            <div class="field">
              <label class="label">{{ $t('sourceTypes.name') }} <span class="has-text-danger">*</span></label>
              <div class="control">
                <input
                  v-model="formData.name"
                  type="text"
                  class="input"
                  :class="{ 'is-danger': validationErrors.name }"
                  :placeholder="$t('sourceTypes.parserv2.namePlaceholder')"
                  @input="validateName"
                />
              </div>
              <p v-if="validationErrors.name" class="help is-danger">
                {{ validationErrors.name }}
              </p>
            </div>

            <div class="field">
              <label class="label">{{ $t('description') }}</label>
              <div class="control">
                <textarea
                  v-model="formData.description"
                  class="textarea"
                  rows="3"
                  :placeholder="$t('sourceTypes.parserv2.descriptionPlaceholder')"
                  maxlength="500"
                />
              </div>
              <p class="help">{{ descriptionCharCount }}/500</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-box-footer">
        <div class="buttons-left">
          <button class="button" @click="handleCancel">
            {{ $t('cancel') }}
          </button>
        </div>
        <div class="buttons-right">
          <button
            v-if="currentStep > 1"
            class="button"
            @click="previousStep"
            :disabled="isLoading"
          >
            {{ $t('sourceTypes.wizard.previous') }}
          </button>
          <button
            v-if="currentStep < 4"
            class="button is-primary"
            @click="nextStep"
            :disabled="!canProceed || isLoading"
          >
            {{ $t('sourceTypes.wizard.next') }}
          </button>
          <button
            v-if="currentStep === 4"
            class="button is-success"
            :class="{ 'is-loading': isLoading }"
            :disabled="!canSave || isLoading"
            @click="saveSourceType"
          >
            {{ isEditMode ? $t('sourceTypes.wizard.update') : $t('sourceTypes.wizard.create') }}
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { Modal } from '@cyradar/ui'
import { check } from 'octicons-vue'

export default {
  name: 'ParserV2Wizard',

  components: {
    Modal,
    Octicon: () => import('octicons-vue').then(m => m.Octicon)
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    editItem: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      check,
      currentStep: 1,

      // Step 1: Parser selection
      availableParsers: [],
      selectedParserType: '',
      loadingParsers: false,

      // Step 2: Log input
      sampleLog: '',
      isParsing: false,
      parseError: '',

      // Step 3: Field mapping
      parsedFields: {},
      fieldMappings: {},

      // Step 5: Final config
      formData: {
        name: '',
        description: '',
        parser_type: '',
        tier: 0,
        field_mapping: {}
      },

      validationErrors: {
        name: ''
      },

      isLoading: false
    }
  },

  computed: {
    selectedParserName() {
      const parser = this.availableParsers.find(p => p.type === this.selectedParserType)
      return parser ? parser.name : this.selectedParserType
    },

    descriptionCharCount() {
      return this.formData.description ? this.formData.description.length : 0
    },

    canProceed() {
      switch (this.currentStep) {
        case 1:
          return !!this.selectedParserType && !!this.sampleLog
        case 2:
        case 3:
          return true
        default:
          return true
      }
    },

    canSave() {
      return this.formData.name && this.formData.name.trim().length > 0 && !this.validationErrors.name
    }
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.initWizard()
      }
    }
  },

  methods: {
    async initWizard() {
      this.currentStep = 1
      this.selectedParserType = ''
      this.sampleLog = ''
      this.parsedFields = {}
      this.fieldMappings = {}
      this.parseError = ''
      this.formData = {
        name: '',
        description: '',
        parser_type: '',
        tier: 0,
        field_mapping: {}
      }
      this.validationErrors = { name: '' }

      // Load available parsers
      await this.loadParsers()

      // If edit mode, populate data
      if (this.isEditMode && this.editItem) {
        await this.populateEditData()
      }
    },

    async loadParsers() {
      this.loadingParsers = true
      try {
        const response = await this.$http.get('/api/v1/source-types/parsers')
        if (response && response.data && response.data.data) {
          this.availableParsers = response.data.data.parsers || []
        }
      } catch (error) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('sourceTypes.parserv2.loadParsersError')
        })
      } finally {
        this.loadingParsers = false
      }
    },

    selectParserType(type) {
      this.selectedParserType = type
      this.formData.parser_type = type

      // Auto-set tier based on parser type
      const tiers = {
        'sophos': 99,
        'f5_asm': 97,
        'fortinet': 96
      }
      this.formData.tier = tiers[type] || 0
    },

    handleParserChange() {
      this.selectParserType(this.selectedParserType)
    },

    async parseLog() {
      if (!this.sampleLog || !this.selectedParserType) {
        return
      }

      this.isParsing = true
      this.parseError = ''

      try {
        const sampleLogs = this.buildSampleLogsFromTextarea(this.sampleLog)
        if (sampleLogs.length === 0) {
          this.parseError = this.$t('sourceTypes.parserv2.parseError')
          return
        }

        const response = await this.$http.post('/api/v1/source-types/parse-with-type', {
          parser_type: this.selectedParserType,
          sample_logs: sampleLogs
        })

        if (response && response.data && response.data.data) {
          const result = response.data.data

          if (result.success) {
            this.parsedFields = result.parsed_fields || {}
            this.parseError = ''

            // Initialize field mappings with suggested values
            this.initializeFieldMappings()
          } else {
            this.parseError = result.error || this.$t('sourceTypes.parserv2.parseError')
            this.parsedFields = {}
          }
        }
      } catch (error) {
        this.parseError = error.response?.data?.message || this.$t('sourceTypes.parserv2.parseError')
        this.parsedFields = {}
      } finally {
        this.isParsing = false
      }
    },

    initializeFieldMappings() {
      this.fieldMappings = {}
      for (const field of Object.keys(this.parsedFields)) {
        // Default to source field name
        this.fieldMappings[field] = field
      }
    },

    getSuggestedMapping(sourceField) {
      // Auto-mapping suggestions based on common field names
      const mappings = {
        'src_ip': 'source.ip',
        'source_ip': 'source.ip',
        'ip_client': 'source.ip',
        'dst_ip': 'destination.ip',
        'dest_ip': 'destination.ip',
        'destination_ip': 'destination.ip',
        'src_port': 'source.port',
        'source_port': 'source.port',
        'dst_port': 'destination.port',
        'dest_port': 'destination.port',
        'destination_port': 'destination.port',
        'device_name': 'observer.name',
        'hostname': 'observer.hostname',
        'unit_hostname': 'observer.hostname',
        'protocol': 'network.protocol',
        'action': 'event.action',
        'enforcement_action': 'event.action',
        'method': 'http.request.method',
        'uri': 'url.path',
        'url': 'url.full',
        'user': 'user.name',
        'username': 'user.name',
        'severity': 'event.severity',
        'severity_code': 'event.severity',
        'timestamp': '@timestamp',
        'date_time': '@timestamp',
        'syslog_timestamp': '@timestamp'
      }

      return mappings[sourceField] || ''
    },

    formatFieldValue(value) {
      if (!value) return '-'
      if (value.length > 20) {
        return value.substring(0, 20) + '...'
      }
      return value
    },

    validateName() {
      if (!this.formData.name || this.formData.name.trim() === '') {
        this.validationErrors.name = this.$t('sourceTypes.validation.nameRequired')
        return false
      }
      this.validationErrors.name = ''
      return true
    },

    async nextStep() {
      if (!this.canProceed) {
        return
      }

      // Auto-parse when moving from step 1 to step 2
      if (this.currentStep === 1) {
        await this.parseLog()
        // Only proceed if parsing was successful
        if (Object.keys(this.parsedFields).length === 0) {
          return
        }
      }

      if (this.currentStep < 4) {
        this.currentStep++
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },

    async saveSourceType() {
      if (!this.validateName()) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('sourceTypes.validation.formHasErrors')
        })
        return
      }

      this.isLoading = true

      // Build field_mapping object
      const fieldMapping = {
        rename: {}
      }

      for (const [source, target] of Object.entries(this.fieldMappings)) {
        if (target && target.trim()) {
          fieldMapping.rename[source] = target.trim()
        }
      }

      const payload = {
        name: this.formData.name,
        description: this.formData.description || '',
        parser_type: this.selectedParserType,
        tier: this.formData.tier,
        field_mapping: fieldMapping,
        sample_logs: this.buildSampleLogsFromTextarea(this.sampleLog)
      }

      try {
        let response
        if (this.isEditMode && this.editItem && this.editItem.id) {
          response = await this.$http.patch(`/api/v1/source-types/${this.editItem.id}`, payload)
        } else {
          response = await this.$http.post('/api/v1/source-types', payload)
        }

        if (response && response.data) {
          this.$store.dispatch('NOTIFY', {
            type: 'success',
            text: response.data.message || this.$t('sourceTypes.messages.saveSuccess')
          })

          this.$emit('success')
          this.handleModalClose(false)
        }
      } catch (error) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('sourceTypes.messages.saveError')
        })
      } finally {
        this.isLoading = false
      }
    },

    handleCancel() {
      this.handleModalClose(false)
    },

    handleModalClose(value) {
      this.$emit('update:show', value)
    },

    async populateEditData() {
      if (!this.editItem) return

      this.formData.name = this.editItem.name || ''
      this.formData.description = this.editItem.description || ''
      this.selectedParserType = this.editItem.parser_type || ''
      this.formData.parser_type = this.editItem.parser_type || ''
      this.formData.tier = this.editItem.tier || 0

      // Populate sample log if available - join all saved sample_logs
      if (this.editItem.sample_logs && this.editItem.sample_logs.length > 0) {
        this.sampleLog = this.editItem.sample_logs.join('\n')

        // Auto-parse the sample log to populate parsedFields
        if (this.selectedParserType && this.sampleLog) {
          await this.parseLog()
        }
      }

      // Populate field mappings after parsing (to ensure parsedFields exists)
      if (this.editItem.field_mapping && this.editItem.field_mapping.rename) {
        // Start with saved mappings (this preserves all saved mappings including those not in current parsed log)
        this.fieldMappings = { ...this.editItem.field_mapping.rename }

        // Add any new fields from parsed log that aren't in saved mappings
        for (const field of Object.keys(this.parsedFields)) {
          if (!this.fieldMappings.hasOwnProperty(field)) {
            this.fieldMappings[field] = field
          }
        }
      }

      // In edit mode, start at step 4 (Final Configuration)
      this.currentStep = 4
    },

    /**
     * Split textarea content into an array of trimmed, non-empty log lines.
     */
    buildSampleLogsFromTextarea(text) {
      return text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
    }
  }
}
</script>

<style scoped>
.wizard-modal .modal-box {
  width: 900px;
  max-width: 95vw;
}

.modal-box-header {
  padding: 1.5rem;
  border-bottom: 1px solid #dbdbdb;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.step-indicator {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.step-text {
  font-size: 0.875rem;
  color: #7a7a7a;
}

.step-dots {
  display: flex;
  gap: 0.5rem;
}

.step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #dbdbdb;
  transition: background-color 0.2s;
}

.step-dot.is-active {
  background-color: #3273dc;
}

.step-dot.is-completed {
  background-color: #48c774;
}

.step-content {
  padding: 2rem;
  min-height: 400px;
}

.step-panel {
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.section-description {
  color: #7a7a7a;
  font-size: 0.875rem;
}

.parser-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.parser-option {
  border: 2px solid #dbdbdb;
  border-radius: 6px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  color: #363636;
}

.parser-option:hover {
  border-color: #3273dc;
  background-color: #f0f7ff;
}

.parser-option.is-selected {
  border-color: #3273dc;
  background-color: #ebf4ff;
}

.parser-name {
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #363636;
}

.parser-type {
  font-size: 0.875rem;
  color: #4a4a4a;
  font-family: monospace;
}

.parser-check {
  color: #3273dc;
}

.log-input-section .textarea {
  font-family: monospace;
  font-size: 0.875rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.field-mapping-table {
  max-height: 400px;
  overflow-y: auto;
}

.field-mapping-table .field-name {
  font-size: 0.875rem;
  background-color: #f5f5f5;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-mapping-table .sample-value {
  font-size: 0.875rem;
  background-color: #f5f5f5;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 80%;
  white-space: nowrap;
}

.preview-summary {
  background-color: #ffffff;
  border: 2px solid #dbdbdb;
  padding: 1.25rem;
  border-radius: 6px;
}

.summary-item {
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-item strong {
  color: #363636;
  font-weight: 600;
}

.summary-item span {
  color: #4a4a4a;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.mapping-list {
  list-style: none;
  padding: 0;
}

.mapping-list li {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f5f5f5;
}

.mapping-list li:last-child {
  border-bottom: none;
}

.modal-box-footer {
  padding: 1.5rem;
  border-top: 1px solid #dbdbdb;
  display: flex;
  justify-content: space-between;
}

.buttons-right {
  display: flex;
  gap: 0.5rem;
}

.empty-state {
  text-align: center;
  padding: 3rem;
}

/* Dark theme support */
.theme-dark .parser-option {
  border-color: #4a4a4a;
  background-color: #2b2b2b;
  color: #e0e0e0;
}

.theme-dark .parser-option:hover {
  background-color: #363636;
  border-color: #3273dc;
}

.theme-dark .parser-option.is-selected {
  background-color: #1a3a5a;
  border-color: #3273dc;
}

.theme-dark .parser-name {
  color: #f5f5f5;
}

.theme-dark .parser-type {
  color: #b0b0b0;
}

.theme-dark .field-mapping-table .field-name {
  background-color: #363636;
}

.theme-dark .field-mapping-table .sample-value {
  background-color: #363636;
}

.theme-dark .preview-summary {
  background-color: #2b2b2b;
  border-color: #4a4a4a;
}

.theme-dark .summary-item strong {
  color: #f5f5f5;
}

.theme-dark .summary-item span {
  color: #b0b0b0;
}
</style>
